// src/pages/ConfluenceTargetPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Stack, Box, Chip, Divider, LinearProgress, Alert, Table,
  TableHead, TableRow, TableCell, TableBody, Button
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { setupSSE } from '../sse';
import RequirementValidationBlock, { normalizeRequirementValidation } from '../components/RequirementValidationBlock';
import {
  buildSourceLabel,
  getRequirementText,
  normalizeRequirement,
  summarizeRequirementMeta,
} from '../utils/requirements';
import { useAuth } from '../auth/AuthContext';

export default function ConfluenceTargetPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { apiClient } = useAuth();

  const [meta, setMeta] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState(() => new Set());

  const toggleFullText = (id) => {
    if (!id) return;
    setExpandedTexts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getFullTextPreview = (text, limit = 8) => {
    if (!text) {
      return { preview: '', truncated: false };
    }

    const words = text.trim().split(/\s+/);
    if (words.length <= limit) {
      return { preview: text, truncated: false };
    }

    return {
      preview: `${words.slice(0, limit).join(' ')}…`,
      truncated: true,
    };
  };

  const sseUrl = useMemo(
    () => `/api/ingest/confluence/requirements/stream?id=${encodeURIComponent(pageId)}`,
    [pageId]
  );

  useEffect(() => {
    let closeSSE = null;

    const init = async () => {
      setLoading(true);
      setError('');
      try {
        // 1) метаданные
        const metaResp = await apiClient.get(`/api/ingest/confluence/target/${encodeURIComponent(pageId)}`);
        setMeta(metaResp.data);

        // 2) уже готовые требования
        const reqResp = await apiClient.get(`/api/ingest/confluence/requirements/${encodeURIComponent(pageId)}`);
        const normalized = Array.isArray(reqResp.data)
          ? reqResp.data.map(normalizeRequirement)
          : [];
        setRequirements(normalized);

        // 3) подписка на SSE
        setStreaming(true);
        closeSSE = setupSSE(
          sseUrl,
          (evt) => {
            try {
              const data = JSON.parse(evt.data || '{}');

              // допустимые типы событий: requirement, batch, progress, done, error
              if (data.type === 'requirement' && data.payload) {
                const requirement = normalizeRequirement(data.payload);
                setRequirements((prev) => {
                  const rid = requirement?.stableId || requirement?.id;
                  const exists = rid ? prev.some((r) => (r?.stableId || r?.id) === rid) : false;
                  return exists ? prev : [...prev, requirement];
                });
              } else if (data.type === 'batch' && Array.isArray(data.payload)) {
                const incoming = data.payload.map(normalizeRequirement);
                setRequirements((prev) => {
                  const merged = [...prev];
                  incoming.forEach((r) => {
                    const rid = r?.stableId || r?.id;
                    const exists = rid ? merged.some((x) => (x?.stableId || x?.id) === rid) : false;
                    if (!exists) merged.push(r);
                  });
                  return merged;
                });
              } else if (data.type === 'done') {
                setDone(true);
                setStreaming(false);
                if (closeSSE) closeSSE();
              } else if (data.type === 'error') {
                setError(data.message || 'Ошибка обработки.');
              }
            } catch (e) {
              console.error('SSE parse error', e);
            }
          },
          (e) => {
            console.error('SSE error', e);
            setStreaming(false);
            if (!done) {
              setError('Потеряна связь с SSE. Требования могут прийти не полностью.');
            }
          }
        );
      } catch (e) {
        console.error(e);
        setError('Не удалось загрузить данные по объекту Confluence.');
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => {
      if (closeSSE) closeSSE();
    };
  }, [pageId, sseUrl, done, apiClient]);

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <Button variant="text" onClick={() => navigate(-1)}>← Назад</Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Объект Confluence
        </Typography>
      </Box>

      {loading && <LinearProgress />}

      {error && <Alert severity="error">{error}</Alert>}

      {meta && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Страница</Typography>
          <Typography sx={{ mb: 1 }}>
            {meta.title ? `${meta.title} (${meta.pageId})` : meta.pageId}
          </Typography>
          {meta.url && (
            <Typography sx={{ mb: 1 }}>
              <a href={meta.url} target="_blank" rel="noreferrer">{meta.url}</a>
            </Typography>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Дата создания</Typography>
          <Typography sx={{ mb: 1 }}>
            {meta.addedAt ? new Date(meta.addedAt).toLocaleString() : '—'}
          </Typography>

          <Box mt={1} display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Статус</Typography>
            {done ? (
              <Chip label="Готово" color="success" size="small" />
            ) : streaming ? (
              <Chip label="Обрабатывается…" color="warning" size="small" />
            ) : (
              <Chip label="Ожидание" size="small" />
            )}
          </Box>
        </Box>
      )}

      <Divider />

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Требования</Typography>

        {!done && streaming && <LinearProgress />}

        {requirements.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Пока нет требований. Они появятся здесь по мере готовности.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Текст требования</TableCell>
                <TableCell>Метаданные</TableCell>
                <TableCell>Валидация</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requirements.map((r, idx) => {
                const rid = r?.stableId || r?.id || `req-${idx}`;
                const fullText = getRequirementText(r);
                const { preview, truncated } = getFullTextPreview(fullText);
                const isExpanded = expandedTexts.has(rid);
                const validation = normalizeRequirementValidation(r);
                const metaEntries = summarizeRequirementMeta(r);
                const sourceLabel = buildSourceLabel(r);

                return (
                  <TableRow key={rid || idx}>
                    <TableCell>{rid || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 440 }}>
                      {fullText ? (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            cursor: truncated || isExpanded ? 'pointer' : 'default',
                            color: truncated || isExpanded ? 'primary.main' : 'text.primary',
                            textDecoration: truncated || isExpanded ? 'underline' : 'none',
                          }}
                          onClick={() => {
                            if (truncated || isExpanded) toggleFullText(rid);
                          }}
                        >
                          {isExpanded ? fullText : preview}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack spacing={0.5}>
                        {metaEntries.length === 0 && !sourceLabel && (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                        {metaEntries.map(({ label, value }) => (
                          <Typography key={label} variant="body2">
                            <b>{label}:</b> {value}
                          </Typography>
                        ))}
                        {sourceLabel && !metaEntries.some((m) => m.label === 'Источник') && (
                          <Typography variant="body2">
                            <b>Источник:</b> {sourceLabel}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <RequirementValidationBlock validation={validation} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </Stack>
  );
}
